import * as React from 'react';
import * as Immutable from 'immutable';

import {renderToStaticMarkup} from 'react-dom/server';

import {createSmartContext} from '@packages/utilities/smart-context';
import Site, {SiteType} from '@packages/systems/core/records/Site';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {StylesMap} from '@packages/systems/style-legacy/utils';
import {
  VariableMap,
  VariableCollectionMap,
  emptyVariableMap,
  emptyVariableCollectionMap,
} from '@packages/systems/core/utils/StyleVariables';
import {cacheMemo, weakMemo, singleMemoMulti} from '@packages/utilities/memo';
import {Lazy, defer, force} from '@packages/utilities/lazy';
import {
  compose,
  emptyArray,
  emptyObject,
  identity,
} from '@packages/utilities/fp/utils';
import {AttributesMap, Plugins} from '@packages/systems/wfdl';
import {
  VariantData,
  VariantDataMap,
  VariantStateKey,
  emptyVariantDataMap,
} from '@packages/systems/style-legacy/selectors';
import {createSelector} from 'reselect';
import {AttributeMap} from './AttributesAndClasses';
import * as KeySet from '@packages/utilities/key-set';
import {
  ActiveVariants,
  BreakpointID,
  ClassNameAndVariantStateKeys,
  ClassNameMap,
  CssPropertyMap,
} from '@packages/systems/style/types';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {getTag} from '@packages/utilities/platform';
import {type StyleBlock} from '@packages/systems/style-legacy/records';
import {type CanvasAssetsWithRenderProperties} from '@packages/systems/designer/canvas';
import {
  GetIx3ElementTargetsReturn,
  defaultStringifiedTargets,
} from '@packages/systems/ix3/shared';
import {FrameOrientationSettings} from '@packages/systems/component-canvas/types';
import {Locale} from '@packages/systems/localization-types';
import {Entitlements} from '@packages/domains/billing/types';
import type {FunctionsPreviewConfig} from '@packages/systems/code-modules/constants/functions';
import {
  defaultContentEditorViewportAdapter,
  ContentEditorViewportAdapter,
} from '@packages/systems/ContentEditorViewportAdapter';
import type {ContentEditorLiveSnapshot} from '@packages/systems/designer/validation';
import type {CodeModuleWorkerUrls} from '@packages/systems/code-modules/core';

type EmptyEnvironmentContext = {environment: 'Empty'};

/**
 * Eventually we won't have this because there is always an environment.
 * This just exists until we port server, editor and llm environments.
 */
export const emptyEnvironment: EmptyEnvironmentContext = {
  environment: 'Empty',
};

type Dispatch = (action: {type: string; payload: unknown}) => void;

/**
 * In preview mode, when on a component page we are only rendering a single
 * frame, indicated by `frameId`. Possible values for `previewMode`:
 * - `null`: we are not in preview mode
 * - `{frameId: null}`: we are in preview mode on a regular page
 * - `{frameId: 'some-id'}`: we are in preview mode on a component page
 */
export type PreviewMode = {frameId: string | null} | null;

/**
 * Rewrites HtmlEmbed content on the Open Canvas before it is rendered.
 * Actual re-writer implementation is provided from the Designer.
 */
export type HtmlEmbedOpenCanvasRewriter = (html: string) => string;

/**
 * Rewrites a native style tag's CSS for Open Canvas rendering. The Designer
 * provides the implementation so shared site-rendering plugins do not bundle
 * the CSS parser.
 */
export type StyleTagOpenCanvasRewriter = (css: string) => string;

type FunctionsPreview = FunctionsPreviewConfig | null;

export type DesignerContext = {
  environment: 'Designer';
  showEmpty: boolean;
  previewMode: PreviewMode;
  mediaQuery: BreakpointID | null;
  isOpenCanvas: boolean;
  optimizationClickMode: boolean;
  showEmptyForBuildModeMap: {
    [key: string]: boolean;
  };
  variantState: VariantStateKey | null;
  variables: VariableMap;
  variableCollections: VariableCollectionMap;
  cssPropertyMap: CssPropertyMap;
  analyzeMode: boolean;
  styleBlocks: Immutable.Map<string, StyleBlock> | null;
  annotationsMode: boolean;
  buildMode: boolean;
  componentPreviewMode: boolean;
  siteComponentVersion: number;
  contentEditorViewportAdapter: ContentEditorViewportAdapter;
  contentEditorFrameId: string | null;
  htmlEmbedOpenCanvasRewriter: HtmlEmbedOpenCanvasRewriter;
  styleTagOpenCanvasRewriter: StyleTagOpenCanvasRewriter;
  functionsPreview: FunctionsPreview;
  omitHiddenElements: boolean;
};

export type SsrContext = {
  environment: 'Ssr';
  tagStylePath: string | null;
  codeModuleWorkerUrlMap: Record<string, CodeModuleWorkerUrls> | null;
  codeComponentsNestedRoot: boolean | null;
  supportsStreamingRendering: boolean;
};

export type RenderEnvironment =
  | DesignerContext
  | EmptyEnvironmentContext
  | SsrContext;

export type IntervalTrackerTimer = {
  finish: (tags?: {[tag: string]: string}) => number;
};

export type RenderIntervalTracker = {
  startTimer: (action: string) => IntervalTrackerTimer;
  trackDuration: (interval: {
    action: string;
    startTime: number;
    endTime: number;
    tags?: {[tag: string]: string};
  }) => void;
  trackAggregates: () => void;
  flush: () => void;
};

export type GetContentEditorLiveSnapshot = () => ContentEditorLiveSnapshot;

export const createDesignerContext = singleMemoMulti(
  (
    showEmpty: boolean,
    previewMode: PreviewMode,
    isOpenCanvas: boolean,
    optimizationClickMode: boolean,
    showEmptyForBuildModeMap: {
      [key: string]: boolean;
    },
    variantState: VariantStateKey | null,
    variables: VariableMap,
    variableCollections: VariableCollectionMap,
    cssPropertyMap: CssPropertyMap,
    analyzeMode: boolean,
    styleBlocks: Immutable.Map<string, StyleBlock>,
    annotationsMode: boolean,
    buildMode: boolean,
    componentPreviewMode: boolean,
    siteComponentVersion: number,
    contentEditorViewportAdapter: ContentEditorViewportAdapter,
    htmlEmbedOpenCanvasRewriter: HtmlEmbedOpenCanvasRewriter,
    mediaQuery: BreakpointID | null | undefined,
    contentEditorFrameId: string | null = null,
    functionsPreview: FunctionsPreview = null,
    styleTagOpenCanvasRewriter: StyleTagOpenCanvasRewriter = identity,
    omitHiddenElements: boolean = false
  ): DesignerContext => {
    return {
      environment: 'Designer',
      showEmpty,
      previewMode,
      mediaQuery: mediaQuery || null,
      isOpenCanvas,
      optimizationClickMode,
      showEmptyForBuildModeMap,
      variantState,
      variables,
      variableCollections,
      cssPropertyMap,
      analyzeMode,
      styleBlocks,
      annotationsMode,
      buildMode,
      componentPreviewMode,
      siteComponentVersion,
      contentEditorViewportAdapter,
      contentEditorFrameId,
      htmlEmbedOpenCanvasRewriter,
      styleTagOpenCanvasRewriter,
      functionsPreview,
      omitHiddenElements,
    };
  }
);

export const createSsrContext = singleMemoMulti(
  (
    tagStylePath: string | null,
    codeModuleWorkerUrlMap: Record<string, CodeModuleWorkerUrls> | null,
    codeComponentsNestedRoot: boolean | null,
    supportsStreamingRendering: boolean
  ): SsrContext => {
    return {
      environment: 'Ssr',
      tagStylePath,
      codeModuleWorkerUrlMap,
      codeComponentsNestedRoot,
      supportsStreamingRendering,
    };
  }
);

const getEnvironment = (context: RenderContext) => context.environment;

export const defaultDesignerContext = createDesignerContext(
  false,
  null,
  false,
  false,
  {},
  null,
  emptyVariableMap,
  emptyVariableCollectionMap,
  Immutable.Map(),
  false,
  Immutable.Map(),
  false,
  false,
  false,
  0,
  defaultContentEditorViewportAdapter,
  identity,
  null
);

const environmentSelector = weakMemo(
  <T,>(selector: (env: RenderEnvironment) => T) =>
    (context: RenderContext) =>
      selector(getEnvironment(context))
);

/**
 * Internal hook for safely accessing the Designer Environment,
 * you can provide a default value to return if the environment
 * is not the Designer.
 *
 * If you don't provide a fallback value, null will be returned forcing
 * consumers of your hook to handle the missing value themselves.
 */
function useEnvironment<T>(selector: (env: RenderEnvironment) => T) {
  return useRenderContext(environmentSelector(selector));
}

const onDesignerEnvironment =
  <T,>(selector: (env: DesignerContext) => T, fallback: T) =>
  (env: RenderEnvironment) =>
    env.environment === 'Designer' ? selector(env) : fallback;

const onSsrEnvironment =
  <T,>(selector: (env: SsrContext) => T, fallback: T) =>
  (env: RenderEnvironment) =>
    env.environment === 'Ssr' ? selector(env) : fallback;

const getShowEmpty = onDesignerEnvironment((env) => env.showEmpty, false);

/**
 * Returns whether or not the user would like to see empty elements in the Designer.
 * This is primarily useful for dropping elements in as children.
 *
 * If the preview mode is not available, for example on the server, return false.
 */
export const useShowEmpty = (): boolean => {
  return useEnvironment(getShowEmpty);
};

/**
 * For the showEmptyForBuildModeMap of stringified native id paths (joined with '-') pointing to elements
 * that should override the showEmpty value to show empty styles, returns the boolean value
 * the given key is mapped to.
 *
 * If the key is not in the map, returns false.
 */
export const useShowEmptyForBuildModeByKey = (id: string): boolean => {
  const getShowEmptyForBuildModeByKey = React.useCallback(
    (env: RenderEnvironment) =>
      onDesignerEnvironment<boolean>(
        (context) => context.showEmptyForBuildModeMap[id] || false,
        false
      )(env),
    [id]
  );
  return useEnvironment(getShowEmptyForBuildModeByKey);
};

const getMediaQuery = onDesignerEnvironment((env) => env.mediaQuery, null);

/**
 * Returns the active Designer breakpoint, or `null` outside the Designer.
 *
 * This subscribes the caller to every breakpoint change, so only use it where
 * the value is always needed (e.g. Frame layout). For per-element render paths
 * prefer `useMediaQueryWhen`, which lets elements that don't depend on the
 * breakpoint skip the re-render when it changes.
 */
export const useMediaQuery = (): BreakpointID | null => {
  return useEnvironment(getMediaQuery);
};

/**
 * Subscribes to the active breakpoint ONLY when `needsMq` returns `true`.
 *
 * When `needsMq` returns `false`, the selector would always return a stable `null`, and would
 * not trigger a re-render on the subscribing component.
 */
export const useMediaQueryWhen = (
  needsMq: (mq: BreakpointID | null) => boolean
): BreakpointID | null => {
  const selector = React.useCallback(
    (env: RenderEnvironment) => {
      const mq = getMediaQuery(env);
      return needsMq(mq) ? mq : null;
    },
    [needsMq]
  );
  return useEnvironment(selector);
};

const getIsOpenCanvas = onDesignerEnvironment((env) => env.isOpenCanvas, false);

/**
 * Returns `true` if the user is working in the Open Canvas (currently only available in the Component Canvas)
 */
export const useOpenCanvas = (): boolean => {
  return useEnvironment(getIsOpenCanvas);
};

const getHtmlEmbedOpenCanvasRewriter = onDesignerEnvironment(
  (env) => env.htmlEmbedOpenCanvasRewriter,
  identity
);

/**
 * Returns the HtmlEmbed Open Canvas rewriter.
 */
export const useHtmlEmbedOpenCanvasRewriter =
  (): HtmlEmbedOpenCanvasRewriter => {
    return useEnvironment(getHtmlEmbedOpenCanvasRewriter);
  };

const getStyleTagOpenCanvasRewriter = onDesignerEnvironment(
  (env) => env.styleTagOpenCanvasRewriter,
  identity
);

/**
 * Returns the native style tag Open Canvas rewriter.
 */
export const useStyleTagOpenCanvasRewriter = (): StyleTagOpenCanvasRewriter => {
  return useEnvironment(getStyleTagOpenCanvasRewriter);
};

const getPreviewMode = onDesignerEnvironment(
  (env) => env.previewMode != null,
  false
);

/**
 * Returns whether or not the user is in Preview Mode in the Designer.
 *
 * If the preview mode is not available, for example on the server, return false.
 */
export const usePreviewMode = (): boolean => {
  return useEnvironment(getPreviewMode);
};

const getPreviewModeFrameId = onDesignerEnvironment(
  (env) => env.previewMode?.frameId ?? null,
  null
);

export const usePreviewModeFrameId = (): string | null => {
  return useEnvironment(getPreviewModeFrameId);
};

const getOptimizationClickMode = onDesignerEnvironment(
  (env) => env.optimizationClickMode,
  false
);

/**
 * Returns whether or not the user is in Preview Mode in the Designer.
 *
 * If the preview mode is not available, for example on the server, return false.
 */
export const useOptimizationClickMode = (): boolean => {
  return useEnvironment(getOptimizationClickMode);
};

const getAnalyzeMode = onDesignerEnvironment((env) => env.analyzeMode, false);

/**
 * Returns whether or not the user is in Analyze Mode in the Designer.
 *
 * If the analyze mode is not available, for example on the server, return false.
 */
export const useAnalyzeMode = (): boolean => {
  return useEnvironment(getAnalyzeMode);
};

const getAnnotationsMode = onDesignerEnvironment(
  (env) => env.annotationsMode,
  false
);

/**
 * Returns whether or not the user is in Annotations Mode in the Designer.
 *
 * If the annotations mode mode is not available, for example on the server, return false.
 */
export const useAnnotationsMode = (): boolean => {
  return useEnvironment(getAnnotationsMode);
};

const getBuildMode = onDesignerEnvironment((env) => env.buildMode, false);

/**
 * Returns whether or not the user is in Build Mode in the Designer.
 *
 * If the build mode mode is not available, for example on the server, return false.
 */
export const useBuildMode = (): boolean => {
  return useEnvironment(getBuildMode);
};

const getComponentPreviewMode = onDesignerEnvironment(
  (env) => env.componentPreviewMode,
  false
);

/**
 * Returns whether or not the user is in Component Preview Mode in the Designer.
 *
 * If the component preview mode is not available, for example on the server, return false.
 */
export const useComponentPreviewMode = (): boolean => {
  return useEnvironment(getComponentPreviewMode);
};

const getCssPropertyMap = onDesignerEnvironment<CssPropertyMap>(
  (env) => env.cssPropertyMap,
  Immutable.Map()
);

export const useCssPropertyMap = (): CssPropertyMap => {
  return useEnvironment(getCssPropertyMap);
};

const getCssVariables = onDesignerEnvironment(
  (env) => env.variables,
  emptyVariableMap
);

export const useCssVariables = (): VariableMap => {
  return useEnvironment(getCssVariables);
};

const getCssVariableCollections = onDesignerEnvironment(
  (env) => env.variableCollections,
  emptyVariableCollectionMap
);

const getCssVariableCollectionsFromRenderContext = (context: RenderContext) =>
  context.variableCollections;

export const useCssVariableCollections = (): VariableCollectionMap => {
  return useEnvironment(getCssVariableCollections);
};

export const useCssVariableCollectionsFromRenderContext =
  (): VariableCollectionMap => {
    return useRenderContext(getCssVariableCollectionsFromRenderContext);
  };

const getVariantState = onDesignerEnvironment((env) => env.variantState, null);

/**
 * Returns the `variantState` representing `ExternalStore.variantState` provided on `DesignerContext`.
 *
 * This value is used by the Renderer to read the active `variantState` associated with a focused component.
 */
export const useVariantState = (): VariantStateKey | null => {
  return useEnvironment(getVariantState);
};

const getSiteComponentVersion = onDesignerEnvironment(
  (env) => env.siteComponentVersion,
  0
);

/**
 * Returns the site component version from the Designer context.
 *
 * If not in Designer environment, returns 0.
 */
export const useSiteComponentVersion = (): number => {
  return useEnvironment(getSiteComponentVersion);
};

const getFunctionsPreview = onDesignerEnvironment(
  (env) => env.functionsPreview,
  null
);

/**
 * Returns the Designer functions-preview config — credentials when they are
 * usable, plus whether waiting can still produce them — or null outside
 * Designer.
 */
export const useFunctionsPreview = (): FunctionsPreview => {
  return useEnvironment(getFunctionsPreview);
};

const getOmitHiddenElements = onDesignerEnvironment(
  (env) => env.omitHiddenElements,
  false
);

/**
 * Returns whether elements hidden by conditional visibility should be fully
 * omitted from the DOM, instead of being rendered with a `display:
 * none`-style hidden class. Always `false` outside the Designer.
 */
export const useOmitHiddenElements = (): boolean => {
  return useEnvironment(getOmitHiddenElements);
};

export type RenderContext = {
  stylesMap: Lazy<StylesMap>;
  classNameMap: ClassNameMap;
  variantDataMap: VariantDataMap;
  ix3ElementTargets: GetIx3ElementTargetsReturn;
  pageId: string;
  idAttrMap: Lazy<Record<string, string>>;
  variableCollections: VariableCollectionMap;
  slugMap: Plugins.PluginsType['slugMap'];
  ssrFeatureFlags: Array<string>;
  entitlements: Partial<Entitlements>;
  assets: CanvasAssetsWithRenderProperties;
  siteSearchIndexMode: boolean;
  collectionIdToPageIdMap: Record<string, string>;
  site: SiteType;
  environment: RenderEnvironment;
  runtimeEnvironment: Environment;
  frameOrientation: FrameOrientationSettings | null;
  // Designer/Data Manager context: when editing CMS items, this will be an array of field IDs
  editTargetFieldIdPath: string[] | null;
  // Localization: current locale when available (SSR/Designer)
  currentLocale: Locale | null;
  dispatch: Dispatch;
  getContentEditorLiveSnapshot?: GetContentEditorLiveSnapshot;
  intervalTrackers: {
    codeIslandSsr?: RenderIntervalTracker;
  };
};

const enrichRenderContextWithContentEditorLiveSnapshot = weakMemo(
  (context: RenderContext) =>
    weakMemo(
      (
        getContentEditorLiveSnapshot: GetContentEditorLiveSnapshot
      ): RenderContext => ({
        ...context,
        getContentEditorLiveSnapshot,
      })
    )
);

export const withContentEditorLiveSnapshot = (
  context: RenderContext,
  getContentEditorLiveSnapshot?: GetContentEditorLiveSnapshot
): RenderContext =>
  getContentEditorLiveSnapshot
    ? enrichRenderContextWithContentEditorLiveSnapshot(context)(
        getContentEditorLiveSnapshot
      )
    : context;

export const createRenderContext = singleMemoMulti(
  (
    stylesMap: Lazy<StylesMap>,
    classNameMap: ClassNameMap,
    variantDataMap: VariantDataMap,
    ix3ElementTargets: GetIx3ElementTargetsReturn,
    slugMap: Plugins.PluginsType['slugMap'],
    ssrFeatureFlags: Array<string>,
    entitlements: Partial<Entitlements>,
    pageId: string,
    idAttrMap: Lazy<Record<string, string>>,
    assets: RenderContext['assets'],
    siteSearchIndexMode: boolean,
    collectionIdToPageIdMap: Record<string, string>,
    site: SiteType,
    environment: RenderEnvironment,
    runtimeEnvironment: Environment,
    frameOrientation: FrameOrientationSettings | null,
    editTargetFieldIdPath: string[] | null,
    currentLocale: Locale | null = null,
    dispatch: Dispatch,
    intervalTrackers: RenderContext['intervalTrackers'],
    variableCollections: VariableCollectionMap
  ): RenderContext => {
    return {
      stylesMap,
      classNameMap,
      variantDataMap,
      ix3ElementTargets,
      pageId,
      idAttrMap,
      variableCollections,
      slugMap,
      ssrFeatureFlags,
      entitlements,
      assets,
      siteSearchIndexMode,
      collectionIdToPageIdMap,
      site,
      environment,
      runtimeEnvironment,
      frameOrientation,
      editTargetFieldIdPath,
      currentLocale,
      dispatch,
      intervalTrackers,
    };
  }
);

export const defaultRenderContext: RenderContext = createRenderContext(
  defer(() => emptyObject),
  emptyObject,
  emptyVariantDataMap,
  defaultStringifiedTargets,
  emptyObject,
  emptyArray,
  emptyObject,
  '',
  defer(() => emptyObject),
  emptyObject,
  false,
  emptyObject,
  Site(),
  emptyEnvironment,
  WebflowEnvironments.DESIGNER,
  null,
  null,
  null,
  (_) => {},
  emptyObject,
  emptyVariableCollectionMap
);

const {
  ContextProvider: RenderContextProvider,
  useContext: useRenderContext,
  Context: RenderContext,
} = createSmartContext(defaultRenderContext);

/**
 * Returns a function that retrieves the current render context and renders the given
 * elements to a string with that context provided. Without this, the context isn't available
 * to the elements which can lead to missing content such as classes.
 *
 * Furthermore, the templates are URI encoded in order to support nested templates. Since
 * browsers treat the first closing </script> tag as the end of the script regardless of
 * depth.
 *
 * A more ideal solution would be to use `template` tags, however, that would require regenerating
 * the Site JS for all currently published sites which is a much larger initiative.
 *
 * See https://github.com/webflow/webflow/pull/68942 for more information.
 */
export const useTemplate = () => {
  const context = React.useContext(RenderContext);

  return React.useCallback(
    (elements: React.ReactNode) =>
      encodeURIComponent(
        renderToStaticMarkup(
          <RenderContext.Provider value={context}>
            {elements}
          </RenderContext.Provider>
        )
      ),
    [context]
  );
};

export {RenderContextProvider, useRenderContext};

const getDispatch = (env: RenderContext) => env.dispatch;

export const useDispatch = (): Dispatch => {
  return useRenderContext(getDispatch);
};

const getContentEditorLiveSnapshot = (context: RenderContext) =>
  context.getContentEditorLiveSnapshot;

export const useContentEditorLiveSnapshot = () => {
  return useRenderContext(getContentEditorLiveSnapshot);
};

const getPageId = (context: RenderContext) => context.pageId;

/**
 * Get the page ID currently being rendered, may be empty if not available.
 */
export const usePageId = (): string => {
  const pageId = useRenderContext(getPageId);
  return pageId || '';
};

const getClassNameMapAndVariantDataMap = createSelector(
  (context: RenderContext) => context.classNameMap,
  (context: RenderContext) => context.variantDataMap,
  (classNameMap, variantDataMap) => ({classNameMap, variantDataMap})
);

// We memoize this because for sites with MANY styles, each call can take up a
// lot of time, but it should only ever need to run a handful of times per
// render since there should only be a handful of unique tags per render.
//
// We use `cacheMemo(100)` which is ~an order of magnitude higher than we should
// ever expect to see.
const getTagStyleVariants = cacheMemo(100)((tagName: string) =>
  weakMemo((classNameMap: ClassNameMap): ClassNameAndVariantStateKeys[] =>
    Object.values(classNameMap).filter(
      (v): v is ClassNameAndVariantStateKeys =>
        typeof v === 'object' && tagName === v.name
    )
  )
);

/**
 * Returns an array of `ClassNameAndVariantStateKeys` from `ClassNameMap`
 * that match the current element's HTML tag name.
 *
 * Note: since tag-based style blocks may exist from a workspace library in addition
 * to the local site's tag-based style blocks, the below filtering logic compares the
 * element's HTML tag name to a style block's `name` which contains the associated tag.
 */
const getTagMatchesFromClassNameMap = (
  element: ExpressionElement,
  classNameMap: ClassNameMap
): ClassNameAndVariantStateKeys[] => {
  const tagName = getTag(element);
  return getTagStyleVariants(tagName)(classNameMap);
};

/**
 * Collects all variant classes and attributes for a  `ClassNameAndVariantStateKeys.variantStateKey`s that are
 * contained within `activeVariants`, which represents the active variants in the
 * current render context.
 */
const collectVariantClassesAndAttributes = (
  entry: string | ClassNameAndVariantStateKeys,
  activeVariants: KeySet.Type,
  variantDataMap: VariantDataMap,
  mut_classes: {[className: string]: true},
  mut_attributes: AttributeMap[]
) => {
  if (typeof entry === 'string') return;

  const loweredVariantDataMap: Map<string, VariantData> = variantDataMap;
  KeySet.forEach(entry.variantStateKeys, (variantStateKey) => {
    if (!KeySet.has(activeVariants, variantStateKey)) return;
    const variantData = loweredVariantDataMap.get(variantStateKey);
    if (variantData == null) return;
    const attributes = variantData.getAttributes();
    if (attributes != null) mut_attributes.push(attributes);
    const variantDataClassName = variantData.getClassName();
    if (variantDataClassName != null) mut_classes[variantDataClassName] = true;
  });
};

const memoGetStyleBlockClassNames = weakMemo((element: ExpressionElement) =>
  weakMemo((styleBlockIds: Array<unknown>) =>
    weakMemo((activeVariants: ActiveVariants) => {
      const buildClass = weakMemo(
        ({
          classNameMap,
          variantDataMap,
        }: {
          classNameMap: ClassNameMap;
          variantDataMap: VariantDataMap;
        }): RenderedStyleBlockAttributes => {
          const allClasses: {[className: string]: true} = {};
          const allAttributes: AttributeMap[] = [];

          for (const id of styleBlockIds) {
            if (typeof id !== 'string') continue;

            const styleBlockClassName = classNameMap[id];
            if (styleBlockClassName == null) continue;

            if (typeof styleBlockClassName === 'string') {
              allClasses[styleBlockClassName] = true;
              continue;
            }

            // Otherwise, we're working with a `ClassNameAndVariantStateKeys`:
            allClasses[styleBlockClassName.name] = true;

            collectVariantClassesAndAttributes(
              styleBlockClassName,
              activeVariants,
              variantDataMap,
              allClasses,
              allAttributes
            );
          }

          // When there are `activeVariants` defined, check for matching `VariantStateKeys`
          // in tag-based style blocks which apply to the current element's HTML tag name.
          if (KeySet.size(activeVariants)) {
            const matchingTagsWithVariants = getTagMatchesFromClassNameMap(
              element,
              classNameMap
            );

            // It's possible for there to be matches for a given tag name across both
            // local site & workspace library tag-based style blocks, so here we collect
            // variant classes and attributes from each matching tag-based block.
            matchingTagsWithVariants.forEach((match) => {
              collectVariantClassesAndAttributes(
                match,
                activeVariants,
                variantDataMap,
                allClasses,
                allAttributes
              );
            });
          }

          const classes = Object.keys(allClasses).join(' ');

          // Perf special-case: Zero attributes to provide, so return a plain
          // `string` of classes.
          //
          // We assume this is the most common scenario.
          if (allAttributes.length === 0) {
            return classes;
          }

          // Perf special-case: Only one `AttributesMap` to provide, so no need to
          // pay the cost of merging with the initial empty map and the associated
          // `.withMutations()` calls.
          //
          // We assume that this is a very common scenario.
          if (allAttributes.length === 1) {
            return {
              classes,
              // The `as AttributesMap` cast here should be safe as we've already
              // bounds-checked the array in the above:
              attributes: allAttributes[0] as AttributesMap,
            };
          }

          return {
            classes,
            // Duplicate variant attributes added by both class and tag based style blocks
            // will be cleaned up by `Immutable.Map`
            attributes: Immutable.Map().withMutations((map) => {
              for (const attrs of allAttributes) {
                map.merge(attrs);
              }
            }) as AttributeMap,
          };
        }
      );
      return compose(buildClass)(getClassNameMapAndVariantDataMap);
    })
  )
);

const createStyleBlockClassNamesSelector = (
  element: ExpressionElement,
  data: unknown,
  activeVariants: ActiveVariants
) => {
  const styleBlockIdsInData =
    data && typeof data === 'object' && 'styleBlockIds' in data;
  const styleBlockIds =
    styleBlockIdsInData && Array.isArray(data.styleBlockIds)
      ? data.styleBlockIds
      : emptyArray;

  return memoGetStyleBlockClassNames(element)(styleBlockIds)(activeVariants);
};

/**
 * The result of `useStyleBlockAttributes`, this type can be either a plain
 * string to represent a `class`-only attribute, or an object with both a
 * `classes` string and a separate `attributes: AttributesMap` field.
 *
 * A plain string is returned for the class case since it is most common and
 * avoids the need to create new object(s).
 */
export type RenderedStyleBlockAttributes =
  | string
  | {
      classes: string;
      attributes: AttributesMap;
    };

export const getEditTargetFieldIdPath = (context: RenderContext) => {
  return context.editTargetFieldIdPath;
};

const getContentEditorViewportAdapter =
  onDesignerEnvironment<ContentEditorViewportAdapter>(
    (env: DesignerContext) => env.contentEditorViewportAdapter,
    defaultContentEditorViewportAdapter
  );

const getContentEditorFrameId = onDesignerEnvironment(
  (env: DesignerContext) => env.contentEditorFrameId,
  null
);

const getNullContentEditorFrameId = () => null;

export const useContentEditorViewportAdapter = () => {
  return useEnvironment(getContentEditorViewportAdapter);
};

export const useContentEditorFrameIdWhen = (
  shouldReadFrameId: boolean
): string | null => {
  return useEnvironment(
    shouldReadFrameId ? getContentEditorFrameId : getNullContentEditorFrameId
  );
};

export const emptyActiveVariants: ActiveVariants = KeySet.empty();

/**
 * Retrieves the class names and possibly an `AttributesMap` for all the
 * `StyleBlock`s associated with a given an element’s evaluated data, and any
 * active style variants (encoded as `VariantStateKey`s).
 *
 * If only classes are needed, a plain string will be returned. If custom
 * attributes are required, an object containing `{classes: string, attributes:
 * AttributesMap}` will be returned.
 *
 * The classes are concatenated into a single string in order to maintain the
 * same reference and prevent re-rendering when classes don’t really change.
 *
 * See also:
 *
 * - `ClassNameMap` - Context-provided data structure which gets class name and
 *   `VariantStateKey`s associated with any `StyleBlock`s that are part of the
 *   element's evaluated `data`.
 * - `VariantDataMap` - Context-provided data structure that associates
 *   `VariantData` class/attribute renderers with `VariantStateKey`s.
 */
export const useStyleBlockAttributes = (
  element: ExpressionElement,
  data: unknown,
  activeVariants: ActiveVariants
): RenderedStyleBlockAttributes => {
  return useRenderContext(
    createStyleBlockClassNamesSelector(element, data, activeVariants)
  );
};

export const useEditTargetFieldIdPath = () => {
  return useRenderContext(getEditTargetFieldIdPath);
};

const getStylesMap = (context: RenderContext) => context.stylesMap;

function getStylesAttribute(elementId: string, stylesMap: StylesMap) {
  const nodeStyles = stylesMap[elementId];
  if (nodeStyles) {
    const stylesParam: Record<string, any> = {};
    for (const key in nodeStyles) {
      const nodeStylesByKey = nodeStyles[key];
      if (nodeStylesByKey) {
        stylesParam[key] = nodeStylesByKey.styles;
      }
    }
    return stylesParam;
  }
  return emptyObject;
}

/**
 * Retrieves the styles for a given element ID from the render context.
 *
 * Atoms that use this hook must declare so in `params` on the Atom.
 *
 * params: {
 *   styles: {
 *      type: 'StylesMap',
 *      excludeInherited: true,
 *      filterProperties: ['textAlign', 'fontFamily'],
 *    },
 *  },
 *
 */
export const useStylesMap = (elementId: string) => {
  const stylesMap = useRenderContext(getStylesMap);
  const {styles} = React.useMemo(
    () => getStylesAttribute(elementId, force(stylesMap)),
    [elementId, stylesMap]
  );
  return styles;
};

const getIx3ElementTargets = (context: RenderContext) =>
  context.ix3ElementTargets;

export const useIx3ElementTargets = () => {
  return useRenderContext(getIx3ElementTargets);
};

const getTagCssPath = onSsrEnvironment(
  (env: SsrContext) => env.tagStylePath,
  null
);
export const useTagCssPath = () => {
  return useEnvironment(getTagCssPath);
};
const getCodeModuleWorkerUrlMap = onSsrEnvironment(
  (env: SsrContext) => env.codeModuleWorkerUrlMap,
  null
);
export const useCodeModuleWorkerUrlMap = () => {
  return useEnvironment(getCodeModuleWorkerUrlMap);
};

const getCodeComponentsNestedRoot = onSsrEnvironment(
  (env: SsrContext) => env.codeComponentsNestedRoot,
  null
);
export const useCodeComponentsNestedRoot = () => {
  return useEnvironment(getCodeComponentsNestedRoot);
};

const getSupportsStreamingRendering = onSsrEnvironment(
  (env: SsrContext) => env.supportsStreamingRendering,
  false
);
export const useSupportsStreamingRendering = () => {
  return useEnvironment(getSupportsStreamingRendering);
};

/**
 * Returns a DOM ID given a NodeID using a map of NodeID -> DOM ID attribute
 * values, derived from the nodes on the current page
 */
export const useIdAttr = (id: null | undefined | string) => {
  const getIdAttr = React.useCallback(
    (context: RenderContext) => (id && force(context.idAttrMap)[id]) || '',
    [id]
  );
  const idAttr = useRenderContext(getIdAttr);
  return idAttr;
};

const getStyleBlocks = onDesignerEnvironment(
  (env: DesignerContext) => env.styleBlocks,
  null
);
export const useStyleBlocks = () => {
  return useEnvironment(getStyleBlocks);
};

/**
 * Returns the idAttrMap from context
 * @param context
 * @returns
 */
const getIdAttrMap = (context: RenderContext) => force(context.idAttrMap);
export const useIdAttrMap = () => {
  return useRenderContext(getIdAttrMap);
};

/**
 * Returns an asset given an ID.
 */
export const useAsset = (id: string) => {
  const getAsset = React.useCallback(
    (context: RenderContext) => context.assets[id],
    [id]
  );
  return useRenderContext(getAsset);
};

/**
 * Returns the assets from context
 */
const getAssets = (context: RenderContext) => context.assets;
export const useAssets = () => {
  return useRenderContext(getAssets);
};

const buildAssetsByCdnUrlMap = weakMemo((assets: RenderContext['assets']) => {
  const assetsByCdnUrl: Record<string, RenderContext['assets'][string]> = {};

  for (const assetId in assets) {
    const asset = assets[assetId];
    if (asset?.cdnUrl) {
      assetsByCdnUrl[asset.cdnUrl] = asset;
    }
  }

  return assetsByCdnUrl;
});

/**
 * Returns an asset or a fallback given a src.
 * Optimized for large asset collections by using O(1) lookup instead of O(n) search.
 */
export const useAssetFallback = (
  asset: RenderContext['assets'][string] | undefined,
  src: string
) => {
  const getAssetFallback = React.useCallback(
    (context: RenderContext) => {
      if (asset) return asset;

      const assetsByCdnUrl = buildAssetsByCdnUrlMap(context.assets);
      return assetsByCdnUrl[src];
    },
    [asset, src]
  );
  return useRenderContext(getAssetFallback);
};

const getSiteSearchIndexMode = (context: RenderContext) =>
  context.siteSearchIndexMode;

/**
 * Returns siteSearchIndexMode.
 */
export const useSiteSearchIndexMode = () => {
  return useRenderContext(getSiteSearchIndexMode);
};

const getCollectionIdToPageIdMap = (context: RenderContext) =>
  context.collectionIdToPageIdMap;

/**
 * Returns the collectionIdToPageIdMap.
 */
export const useCollectionIdToPageIdMap = () => {
  return useRenderContext(getCollectionIdToPageIdMap);
};

const getSite = (context: RenderContext) => context.site;

/*
 * Returns the `site` property.
 */
export const useSite = () => {
  return useRenderContext(getSite);
};

const getRuntimeEnvironment = (context: RenderContext) =>
  context.runtimeEnvironment;

/*
 * Returns the `runtimeEnvironment` property.
 */
export const useRuntimeEnvironment = () => {
  return useRenderContext(getRuntimeEnvironment);
};

const getSlugMap = (context: RenderContext) => context.slugMap;

export const useSlugMap = (): Plugins.PluginsType['slugMap'] => {
  return useRenderContext(getSlugMap);
};

const getSSRFeatureFlags = (context: RenderContext) => context.ssrFeatureFlags;

const useSSRFeatureFlags = (): Array<string> => {
  return useRenderContext(getSSRFeatureFlags);
};

const memoizedHasFlag = weakMemo((flags: Array<string>) =>
  cacheMemo(10)((flag: string) => {
    return flags.includes(flag);
  })
);

export const useHasSSRFeatureFlag = (flag: string): boolean => {
  const featureFlags = useSSRFeatureFlags();
  return memoizedHasFlag(featureFlags)(flag);
};

const getEntitlements = (context: RenderContext) => context.entitlements;

export const useEntitlements = (): Partial<Entitlements> => {
  return useRenderContext(getEntitlements);
};

const getFrameOrientation = (context: RenderContext) =>
  context.frameOrientation;

/**
 * Returns the frame orientation settings from the render context.
 */
export const useFrameOrientation = (): FrameOrientationSettings | null => {
  return useRenderContext(getFrameOrientation);
};

const getCurrentLocale = (context: RenderContext) => context.currentLocale;

/**
 * Returns the current locale from the render context.
 */
export const useCurrentLocale = (): Locale | null => {
  return useRenderContext(getCurrentLocale);
};

const getIntervalTrackers = (context: RenderContext) =>
  context.intervalTrackers;
const emptyIntervalTracker: RenderIntervalTracker = {
  startTimer: () => ({finish: () => 0}),
  trackDuration: () => {},
  trackAggregates: () => {},
  flush: () => {},
};

/**
 * Returns the interval tracker with the matching name.
 */
export const useIntervalTracker = (
  name: keyof RenderContext['intervalTrackers']
): RenderIntervalTracker => {
  const intervalTrackers = useRenderContext(getIntervalTrackers);
  return intervalTrackers[name] || emptyIntervalTracker;
};
